import { TZDate, TZDateMini } from '@date-fns/tz';
import { isAfter, isBefore } from 'date-fns';
import { type Dispatch } from 'react';
import { FormattedMessage } from 'react-intl';

import { formatDate, parseTimeAsTimeValue } from '@dynatrace-sdk/units';
import {
  getRegionalFormat,
  getTimezone,
} from '@dynatrace-sdk/user-preferences';

import type { TimeValue, TimeframeV2 } from '../../../core/types/time.js';
import {
  FormFieldMessagesAction,
  type FormFieldMessagesReducerAction,
} from '../../forms-core/state/formfield-messages-state-reducer.js';
import { isTimeframeV2 } from '../../forms-core/time/types.js';
import type { CalendarState } from '../types/shared-types.js';

/** @internal */
export function _isOutOfBounds(
  date?: string | Date,
  min?: string | Date,
  max?: string | Date,
) {
  const newDate =
    typeof date === 'string' ? parseTimeAsTimeValue(date)?.absoluteDate : date;

  if (!newDate) {
    return false;
  }

  // All parse times here need to use the same _now_ date, to not be subject
  // to millisecond differences during parsing.
  const relativeNow = TZDateMini.tz(getTimezone()).getTime();

  const minDate = parseTimeAsTimeValue(
    typeof min === 'object' ? min.toISOString() : min,
    relativeNow,
  )?.absoluteDate;
  const maxDate = parseTimeAsTimeValue(
    typeof max === 'object' ? max.toISOString() : max,
    relativeNow,
  )?.absoluteDate;

  if (minDate && maxDate) {
    return isBefore(newDate, minDate) || isAfter(newDate, maxDate);
  }

  if (minDate && !maxDate) {
    return isBefore(newDate, minDate);
  }

  if (!minDate && maxDate) {
    return isAfter(newDate, maxDate);
  }

  return false;
}

/**
 * @param term - word used to describe the selection. Date for single selection and timeframe for a range.
 * @internal
 */
function getOutOfBoundsError(term: string, min?: TZDate, max?: TZDate) {
  if (min && max) {
    return (
      <FormattedMessage
        id="LNWPcycjuDPimFKd"
        defaultMessage="Please select a {term} within the range of {min} and {max}."
        description="Error message for a date or a timeframe outside the selectable range."
        values={{
          min: formatDate(min, {
            locale: getRegionalFormat(),
            timeZone: getTimezone(),
          }),
          max: formatDate(max, {
            locale: getRegionalFormat(),
            timeZone: getTimezone(),
          }),
          term,
        }}
      />
    );
  }

  if (min) {
    return (
      <FormattedMessage
        id="9XjoWQoeIKxStpgZ"
        defaultMessage="Please select a {term} later than {min}."
        description="Error message for a date or a timeframe earlier than the required minimum."
        values={{ min: min.toLocaleDateString(), term }}
      />
    );
  }

  if (max) {
    return (
      <FormattedMessage
        id="0Y8wN2jk+BAa2NoC"
        defaultMessage="Please select a {term} earlier than {max}."
        description="Error message for a date or a timeframe later then the expected maximum."
        values={{ max: max.toLocaleDateString(), term }}
      />
    );
  }
}

function dispatchMinMaxError(
  formMessageDispatch: Dispatch<FormFieldMessagesReducerAction>,
  errorId?: string,
  showError?: boolean,
  min?: TZDate,
  max?: TZDate,
) {
  const id = errorId ?? `strato-${crypto.randomUUID()}`;

  formMessageDispatch({
    type: FormFieldMessagesAction.ADDMESSAGE,
    payload: {
      id,
      variant: 'error',
      messageType: 'formcontrol',
      message: getOutOfBoundsError('timeframe', min, max),
      validityStateKey: 'customError',
      showError: showError,
    },
  });

  return id;
}

/** @internal */
export function validateCalendarSelection(
  updatedValue: Partial<TimeframeV2> | TimeValue | null,
  state: CalendarState,
  formMessageDispatch: Dispatch<FormFieldMessagesReducerAction>,
  showError?: boolean,
) {
  if (!isTimeframeV2(updatedValue, state.type)) {
    if (_isOutOfBounds(updatedValue?.absoluteDate, state.min, state.max)) {
      const { errorId } = state;

      return dispatchMinMaxError(
        formMessageDispatch,
        errorId,
        showError,
        state.min,
        state.max,
      );
    }
  }

  if (isTimeframeV2(updatedValue, state.type)) {
    if (
      _isOutOfBounds(updatedValue?.from?.absoluteDate, state.min, state.max) ||
      _isOutOfBounds(updatedValue?.to?.absoluteDate, state.min, state.max)
    ) {
      const { errorId } = state;

      return dispatchMinMaxError(
        formMessageDispatch,
        errorId,
        showError,
        state.min,
        state.max,
      );
    }
  }

  // Clean up old errors
  if (state.errorId) {
    formMessageDispatch({
      type: FormFieldMessagesAction.REMOVEMESSAGE,
      payload: {
        id: state.errorId,
        messageType: 'formcontrol',
      },
    });
  }
}
